import { LabeledValue, LabeledValueWithFieldInfo } from "./../types/user"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { TemplateDetails } from "@/types"
import { LabelFormat } from "@/types/insights"
import { padStart } from "lodash"
import moment from "moment"
import { Component, Vue } from "vue-property-decorator"

export const formatFieldsMixin = {
	methods: {
		/**
		 * Formats the value of the field as a string depending on the data type
		 * @param dataType  Data type of the field
		 * @param fieldValue The fields value
		 * @param dataSourceItems Items from a datasource that is used by the field
		 */
		formatFieldValue (dataType: string, fieldValue: any, dataSourceItems: any[] = []) {
			// Format custom field values based of dataType
			switch (dataType) {
				case "date":
					return formatDateMixin.methods.displayDateOnlyFromISOString(fieldValue)
				case "date-time":
					return formatDateMixin.methods.displayDateFromISOString(fieldValue)
				case "dollar":
					return "$" + fieldValue
				case "checkbox":
					return fieldValue ? "Yes" : "No"
				case "list":
					var option = dataSourceItems ? dataSourceItems.find((s: any) => s.id === fieldValue) : null
					return option ? option.label : ""
				case "list-multiple":
					return Array.isArray(fieldValue)
						? fieldValue.map((v: any) => v.label).toString()
						: ""
				default:
					return fieldValue
			}
		},

		formatLabel (format: LabelFormat, label: string): string {
			switch (format) {
				case LabelFormat.Date:
					if (!label || label === "Not Set") return "Not set"

					const momentDate = moment(label)
					return momentDate.isValid() ? momentDate.format("L") : label
				case LabelFormat.WeekDate:
					if (!label || label === "Not Set") return "Not set"

					const momentWeek = moment(label)
					return momentWeek.isValid() ? `Week of ${momentWeek.format("L")}` : label
				case LabelFormat.WeekRange:
					if (!label || label === "Not Set") return "Not set"

					return "Week " + this.weekNumber(new Date(label)) // Do not use moment's ISO Weeks as it incorrectly uses week start
				case LabelFormat.MonthDate:
					if (!label || label === "Not Set") return "Not set"

					const momentMonth = moment(label)
					return momentMonth.isValid() ? `${momentMonth.format("MMMM")} ${momentMonth.format("YYYY")}` : label
				default:
					return label === null || label === "" ? "None" : label
			}
		},

		getDisplayLabelFromLabeledValue (labeledValue: LabeledValue, dataType: string): string {
			if (labeledValue.label !== undefined && labeledValue.label !== null) {
				return labeledValue.label
			}

			if (dataType === "checkbox") {
				if (labeledValue.value) {
					return "Yes"
				} else {
					return "No"
				}
			} else if (dataType === "map-path") {
				if (labeledValue.value) {
					return "Map Path"
				}
				return ""
			} else if (dataType === "map-marker") {
				if (labeledValue.value) {
					return "Map Marker"
				}
				return ""
			} else if (dataType === "map-region") {
				if (labeledValue.value) {
					return "Map Region"
				}
				return ""
			} else if (dataType === "list-multiple") {
				if (labeledValue.value) {
					const listMultipleLabel = labeledValue.value.map((c: any) => c.label).join(", ")
					return listMultipleLabel
				}
				return ""
			} else if (dataType === "list-data") {
				if (labeledValue.value) {
					const listDataLabel = labeledValue.value.map((c: any) => c.label).join(", ")
					return listDataLabel
				}
				return ""
			} else if (dataType === "date-time") {
				return formatDateMixin.methods.displayDateFromISOString(labeledValue.value)
			} else if (dataType === "date") {
				return formatDateMixin.methods.displayDateOnlyFromISOString(labeledValue.value)
			} else if (labeledValue.value !== undefined && labeledValue.value != null) {
				return labeledValue.value
			} else {
				return ""
			}
		},

		getDisplayLabelFromLabeledValueWithFieldInfo (labeledValue: LabeledValueWithFieldInfo): string {
			const dataType = labeledValue.dataType
			const label = this.getDisplayLabelFromLabeledValue(labeledValue as LabeledValue, dataType)
			return label
		},

		weekNumber (weekStart: Date): number {
			const firstDayOfWeek = moment.localeData().firstDayOfWeek()
			const jan4 = new Date(weekStart.getFullYear(), 1, 4)

			const correction = jan4.getDay() + 3
			const dayOfYear = this.dayOfYear(weekStart)
			const weekNumber = Math.floor((dayOfYear + correction - firstDayOfWeek) / 7)

			return weekNumber <= 0 ? 53 : weekNumber
		},

		dayOfYear (date: Date): number {
			var start = new Date(date.getFullYear(), 0, 0)

			// Plus before the date gets around "The right-hand side of an arithmetic operation must be of type ... " error
			var diff = (+date - +start) + ((start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000)
			var oneDay = 1000 * 60 * 60 * 24
			return Math.floor(diff / oneDay)
		},

		formatWeekStart (weekStart: Date): string {
			const momentDate = moment(weekStart)
			if (momentDate.isValid()) {
				return momentDate.format("L")
			}
			return ""
		},

		formatWeekEnd (weekStart: Date): string {
			const momentDate = moment(weekStart).add(6, "days")
			if (momentDate.isValid()) {
				return momentDate.format("L")
			}
			return ""
		},

		showField (template: TemplateDetails, fieldKey: string): boolean {
			if (!template) return false
			if (!template.hiddenFields) return true
			return !template.hiddenFields!.includes(fieldKey)
		},

		timeInHoursAndMinutes (timeValue: number | null | undefined) {
			if (timeValue === null || timeValue === undefined) return ""
			const hours = Math.trunc(timeValue)
			const minutes = Math.round((timeValue % 1) * 60)

			return hours.toString() + ":" + padStart(minutes.toString(), 2, "0")
		}
	}
}

@Component({})
export class FormatFieldsMixin extends Vue {
	/**
	 * Formats the value of the field as a string depending on the data type
	 * @param dataType  Data type of the field
	 * @param fieldValue The fields value
	 * @param dataSourceItems Items from a datasource that is used by the field
	 */
	formatFieldValue (dataType: string, fieldValue: any, dataSourceItems: any[] = []) {
		return formatFieldsMixin.methods.formatFieldValue(dataType, fieldValue, dataSourceItems)
	}

	formatLabel (format: LabelFormat, label: string) {
		return formatFieldsMixin.methods.formatLabel(format, label)
	}

	getDisplayLabelFromLabeledValue (labeledValue: LabeledValue, dataType: string) {
		return formatFieldsMixin.methods.getDisplayLabelFromLabeledValue(labeledValue, dataType)
	}

	getDisplayLabelFromLabeledValueWithFieldInfo (labeledValue: LabeledValueWithFieldInfo) {
		return formatFieldsMixin.methods.getDisplayLabelFromLabeledValueWithFieldInfo(labeledValue)
	}

	weekNumber (weekStart: Date) {
		return formatFieldsMixin.methods.weekNumber(weekStart)
	}

	dayOfYear (date: Date) {
		return formatFieldsMixin.methods.dayOfYear(date)
	}

	formatWeekStart (weekStart: Date) {
		return formatFieldsMixin.methods.formatWeekStart(weekStart)
	}

	formatWeekEnd (weekStart: Date) {
		return formatFieldsMixin.methods.formatWeekEnd(weekStart)
	}

	showField (template: TemplateDetails, fieldKey: string) {
		return formatFieldsMixin.methods.showField(template, fieldKey)
	}

	timeInHoursAndMinutes (timeValue: number | null | undefined) {
		return formatFieldsMixin.methods.timeInHoursAndMinutes(timeValue)
	}
}
